import React, { useCallback, useEffect } from 'react';
import { Modal, Tappable } from '@vectord/components';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import cogoToast from 'cogo-toast';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  MANAGE_MENTEE_MODULES_PAGE_TRACKINGS,
  selectedMenteeModuleSelector,
  setSelectedMenteeModule,
  setUpdateMenteeModuleModalState,
  updateMenteeModuleStatusModalStateSelector,
  useUpdateMenteeModuleStatusMutation,
} from 'features/moduleManagement';
import analytics from 'utils/analytics';
import GlobalLoader from 'components/GlobalLoader';

function UpdateMenteeModuleStatusModal() {
  const dispatch = useDispatch();
  const { email } = useParams();
  const isModalOpen = useSelector(updateMenteeModuleStatusModalStateSelector);
  const selectedMenteeModule = useSelector(selectedMenteeModuleSelector);
  const [updateMenteeModuleStatus, { error, isSuccess, isLoading }] =
    useUpdateMenteeModuleStatusMutation();

  const { updatedStatus, status } = selectedMenteeModule || {};

  const handleOnClose = useCallback(() => {
    batch(() => {
      dispatch(setUpdateMenteeModuleModalState(false));
      dispatch(setSelectedMenteeModule(null));
    });
  }, [dispatch]);

  const handleConfirm = useCallback(() => {
    analytics.click({
      click_type: MANAGE_MENTEE_MODULES_PAGE_TRACKINGS.menteeModuleStatusChange,
      custom: {
        mentee_module: selectedMenteeModule,
        mentee_email: email,
      },
    });

    updateMenteeModuleStatus(selectedMenteeModule);
  }, [selectedMenteeModule, updateMenteeModuleStatus, email]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Status changed successfully!');
      batch(() => {
        dispatch(setUpdateMenteeModuleModalState(false));
        dispatch(setSelectedMenteeModule(null));
      });
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      cogoToast.error(error?.data?.message || DEFAULT_ERROR_MESSAGE);
    }
  }, [error]);

  return (
    <Modal
      isOpen={isModalOpen}
      unMountOnClose
      onClose={handleOnClose}
      withoutHeader
      className="p-xs"
      canClose={!isLoading}
    >
      <GlobalLoader isVisible={isLoading} />
      <div className="bold dark">Confirm Status Change</div>
      <div>
        Are you sure you want to change the status of this module from {status}{' '}
        to {updatedStatus}?
      </div>
      <div className="m-t-xxs row">
        <Tappable
          onClick={handleConfirm}
          disabled={isLoading}
          className="btn btn-primary btn-long m-r-xs"
        >
          Yes
        </Tappable>
        <Tappable
          onClick={handleOnClose}
          disabled={isLoading}
          className="btn btn-inverted btn-long"
        >
          No
        </Tappable>
      </div>
    </Modal>
  );
}

export default UpdateMenteeModuleStatusModal;
